import { createContext, useContext, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { UploadPanel } from '@/features/upload/UploadPanel';

type Dock = { center: HTMLElement | null; setCenter: (el: HTMLElement | null) => void };
const DockCtx = createContext<Dock | null>(null);

/** Wraps the workspace shell so pages inside `<Outlet>` can render into the dock (`DockSlot`). */
export function DockProvider({ children }: { children: ReactNode }) {
  const [center, setCenter] = useState<HTMLElement | null>(null);
  return <DockCtx.Provider value={{ center, setCenter }}>{children}</DockCtx.Provider>;
}

/**
 * One strip along the bottom of the workspace: the selection toolbar sits centred in whatever width the
 * upload tray leaves free, the tray keeps the right edge. Both are flex children, so they can never
 * overlap (the old toolbar was `left-1/2` and the tray `right-4`: at 1280px they collided). Below `sm`
 * the two stack, toolbar above tray.
 */
export function BottomDock() {
  const dock = useContext(DockCtx);
  return (
    <div data-bottom-dock className="pointer-events-none fixed inset-x-4 bottom-4 z-30 flex flex-col items-center gap-2 sm:flex-row sm:items-end sm:gap-3">
      <div ref={dock?.setCenter} data-dock-center className="flex min-w-0 max-w-full flex-1 justify-center" />
      <UploadPanel docked />
    </div>
  );
}

/** Renders into the dock's centre slot; outside a dock (share pages) it falls back to a fixed bottom-centre box. */
export function DockSlot({ children }: { children: ReactNode }) {
  const dock = useContext(DockCtx);
  if (dock?.center) return createPortal(children, dock.center);
  return <div className="pointer-events-none fixed bottom-4 left-1/2 z-30 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 justify-center">{children}</div>;
}
